import { defineStore } from 'pinia'
import { ref } from 'vue'
import { SPORTS, MARKETS, BET_TYPES, BET_TIMING, RISK, CONTENT } from '@/config/onboarding.js'
import { FEED_ITEM_TYPES } from '@/config/feedItemTypes'
import { savePreferences } from '@/services/account.js'
import { Sentry } from '@/services/sentry'

export const useOnboardingStore = defineStore('onboarding', () => {
  // State
  const sports = ref(SPORTS.map(sport => ({ ...sport })))

  const markets = ref(MARKETS.map(market => ({ ...market })))

  const betTypes = ref(BET_TYPES.map(item => ({ ...item })))

  const betTiming = ref(BET_TIMING.map(item => ({ ...item })))

  const risk = ref(RISK.map(item => ({ ...item })))

  const content = ref(CONTENT.map(item => ({ ...item })))

  const feedTypes = ref(FEED_ITEM_TYPES.map((item) => ({ name: item.type, enabled: true })))

  // Tracks if user has completed onboarding flow
  // Use cases: route guards, skipping onboarding for returning users, conditional UI
  const isCompleted = ref(false)

  // Tracks if preferences are currently being saved (to coordinate with fetch operations)
  const isSaving = ref(false)

  // Getters
  const getSelectedSports = () => {
    return sports.value.filter(sport => sport.enabled)
  }

  const getSelectedMarkets = () => {
    return markets.value.filter(market => market.enabled)
  }

  const getSelectedBetTypes = () => {
    return betTypes.value.filter(item => item.enabled)
  }

  const getSelectedBetTiming = () => {
    return betTiming.value.filter(item => item.enabled)
  }

  const getSelectedRisk = () => {
    return risk.value.filter(item => item.enabled)
  }

  const getSelectedContent = () => {
    return content.value.filter(item => item.enabled)
  }

  // Actions
  const updateSports = (updatedSports) => {
    sports.value = updatedSports
  }

  const updateMarkets = (updatedMarkets) => {
    markets.value = updatedMarkets
  }

  const updateBetTypes = (updatedItems) => {
    betTypes.value = updatedItems
  }

  const updateBetTiming = (updatedItems) => {
    betTiming.value = updatedItems
  }

  const updateRisk = (updatedRisk) => {
    risk.value = updatedRisk
  }

  const updateContent = (updatedContent) => {
    content.value = updatedContent
  }

  const updateFeedTypes = (updatedFeedTypes) => {
    feedTypes.value = updatedFeedTypes
  }

  // Populate onboarding data from API preferences
  // Backend returns objects with boolean values: { to_win: false, total_goals: true, ... }
  const populateFromPreferences = (preferences) => {
    if (!preferences) return

    // Populate sports (API returns object: { football: false, basketball: true, ... })
    if (preferences.sports && typeof preferences.sports === 'object') {
      sports.value = sports.value.map(sport => ({
        ...sport,
        enabled: preferences.sports[sport.name] || false
      }))
    }

    // Populate markets (API returns object: { to_win: false, total_goals: true, ... })
    if (preferences.markets && typeof preferences.markets === 'object') {
      markets.value = markets.value.map(market => ({
        ...market,
        enabled: preferences.markets[market.name] || false
      }))
    }

    // Populate bet types (API returns object: { singles: false, acca_doubles: true, ... })
    if (preferences.bet_types && typeof preferences.bet_types === 'object') {
      betTypes.value = betTypes.value.map(type => ({
        ...type,
        enabled: preferences.bet_types[type.name] || false
      }))
    }

    // Populate bet timing (API returns object: { live: false, pre_game: true })
    if (preferences.bet_timing && typeof preferences.bet_timing === 'object') {
      betTiming.value = betTiming.value.map(timing => ({
        ...timing,
        enabled: preferences.bet_timing[timing.name] || false
      }))
    }

    // Populate risk (API returns object: { low: false, medium: true, ... })
    if (preferences.risk && typeof preferences.risk === 'object') {
      risk.value = risk.value.map(item => ({
        ...item,
        enabled: preferences.risk[item.name] || false
      }))
    }

    // Populate content (API returns object: { detailed_previews: false, visual_stats: true, ... })
    if (preferences.content && typeof preferences.content === 'object') {
      content.value = content.value.map(item => ({
        ...item,
        enabled: preferences.content[item.name] || false
      }))
    }

    if (preferences.feed_types && typeof preferences.feed_types === 'object') {
      feedTypes.value = feedTypes.value.map((feedType) => ({
        ...feedType,
        enabled: preferences.feed_types[feedType.name] ?? true
      }))
    }
  }

  // Resets all onboarding data to initial state
  // Use cases: logout, "reset preferences" feature, restart onboarding, testing/development
  const reset = () => {
    sports.value = SPORTS.map(sport => ({ ...sport }))
    markets.value = MARKETS.map(market => ({ ...market }))
    betTypes.value = BET_TYPES.map(item => ({ ...item }))
    betTiming.value = BET_TIMING.map(item => ({ ...item }))
    risk.value = RISK.map(item => ({ ...item }))
    content.value = CONTENT.map(item => ({ ...item }))
    feedTypes.value = FEED_ITEM_TYPES.map((item) => ({ name: item.type, enabled: true }))
    isCompleted.value = false
  }

  const submitOnboarding = async () => {
    // Convert array selections to backend object format
    // Frontend: [{ name: 'to_win', enabled: true }, ...]
    // Backend: { to_win: true, total_goals: false, ... }

    const preferences = {
      sports: sports.value.reduce((obj, type) => {
        obj[type.name] = type.enabled
        return obj
      }, {}),
      markets: markets.value.reduce((obj, market) => {
        obj[market.name] = market.enabled
        return obj
      }, {}),
      bet_types: betTypes.value.reduce((obj, type) => {
        obj[type.name] = type.enabled
        return obj
      }, {}),
      bet_timing: betTiming.value.reduce((obj, timing) => {
        obj[timing.name] = timing.enabled
        return obj
      }, {}),
      risk: risk.value.reduce((obj, item) => {
        obj[item.name] = item.enabled
        return obj
      }, {}),
      content: content.value.reduce((obj, item) => {
        obj[item.name] = item.enabled
        return obj
      }, {}),
      feed_types: feedTypes.value.reduce((obj, feedType) => {
        obj[feedType.name] = feedType.enabled
        return obj
      }, {})
    }

    isSaving.value = true
    try {
      // Save preferences via API
      const response = await savePreferences(preferences)

      isCompleted.value = true
      return response
    } catch (error) {
      console.error('Failed to save onboarding preferences:', error)
      Sentry.captureException(error)
      throw error
    } finally {
      isSaving.value = false
    }
  }

  // Helper to set completion status based on preferences data from backend
  const setCompletionFromPreferences = (preferences) => {
    // If preferences exist (not null), user has completed onboarding
    // User can complete onboarding with zero selections (by clicking Skip on all steps)
    // Backend returns null for new users who haven't gone through onboarding yet
    // Backend returns object (even with all false values) for users who completed onboarding
    if (!preferences) {
      console.log('❌ No preferences object - user has not completed onboarding, setting isCompleted = false')
      isCompleted.value = false
      return
    }

    isCompleted.value = true
  }

  return {
    // State
    sports,
    markets,
    betTypes,
    betTiming,
    risk,
    content,
    feedTypes,
    isCompleted,
    isSaving,

    // Getters
    getSelectedSports,
    getSelectedMarkets,
    getSelectedBetTypes,
    getSelectedBetTiming,
    getSelectedRisk,
    getSelectedContent,

    // Actions
    updateSports,
    updateMarkets,
    updateBetTypes,
    updateBetTiming,
    updateRisk,
    updateContent,
    updateFeedTypes,
    populateFromPreferences,
    setCompletionFromPreferences,
    reset,
    submitOnboarding
  }
})
